import { SubmitHandler } from "react-hook-form";
import { useClassWizardForm } from "./useClassWizardForm";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { PageMode } from "../../../../../common/types";
import { useAppContext } from "../../../../../AppContext";
import { normalizeClassWizardOptions } from "../utils/classWizardOptionsUtility";
import { getClassWizardSavedRoute } from "../utils/classWizardSavedRouteUtility";
import { useApiMutation } from "../../../../../common/infrastructure/hooks/useApiMutation";
import { useApiQuery } from "../../../../../common/infrastructure/hooks/useApiQuery";
import { ISelectFieldModel } from "../../../../../common/components/forms/inputs/dropdown/models/selectFieldModel";
import { endpointsMetadata } from "../../../../../common/infrastructure/routes/endpointsMetadata";
import { IEventWizardInitialData } from "../../models/eventWizardInitialDataModel";
import { IClassWizardEventTemplate } from "../models/classWizardEventTemplate";
import { showCustomConfirmDialog } from "../../../../../common/components/dialogs/AppConfirmDialog";
import {
  getDefaultRoutePath,
  routePaths,
} from "../../../../../common/infrastructure/routes/routePaths";
import { useAddAdditionalLayoutButton } from "../../../../../common/infrastructure/hooks/useAddAdditionalLayoutButton";
import { IconType } from "../../../../../common/icons/Icon";
import {
  ALL_PLAN_TYPES_SENTINEL,
  IClassWizardFormModel,
  NONE_PLAN_TYPES_SENTINEL,
} from "../models/classWizardFormModel";
import {
  EventType,
  PaymentTypes,
  SubscriptionPaymentMode,
} from "../../../../../common/enums";
import { toZonedTime } from "date-fns-tz";
import { useMeetingLinkRecipientsWarning } from "../../../common/hooks/useMeetingLinkRecipientsWarning";

interface IUseEventWizardPageProps {
  pageMode: PageMode;
  initialData?: IEventWizardInitialData;
  studioInstructors?: ISelectFieldModel[];
}

export const useClassWizardPage = ({
  pageMode,
  initialData,
  studioInstructors,
}: IUseEventWizardPageProps) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { currentUser } = useAppContext();
  const { day, month, year } = useParams();
  const [selectedDate, setSelectedDate] = useState<Date | undefined>(undefined);
  const { mutate } = useApiMutation();
  const { warnClientsWithoutEmail } = useMeetingLinkRecipientsWarning();

  useEffect(() => {
    if (year && month && day) {
      const date = new Date(+year, +month - 1, +day, 11);
      setSelectedDate(date);
    }
  }, [year, month, day]);

  const form = useClassWizardForm(
    selectedDate,
    studioInstructors,
    initialData,
    pageMode
  );

  const { data: eventTemplates } = useApiQuery<IClassWizardEventTemplate[]>({
    queryKey: [endpointsMetadata.planner.wizardGetEventTemplates],
    endpoint: `${
      endpointsMetadata.planner.wizardGetEventTemplates
    }?currentlySelectedDate=${selectedDate?.toISOString()}`,
    enabled: !id && !!form && !!selectedDate,
    globalIsLoading: false,
  });

  const deleteEvent = useCallback(() => {
    mutate({
      endpoint: endpointsMetadata.planner.deleteEvent,
      method: "delete",
      invalidateKeys: [[endpointsMetadata.planner.getAll]],
      additionalParams: `itemId=${id}`,
    });
    navigate(routePaths.calendar);
  }, [id, mutate, navigate]);

  const deleteEventConfirm = useCallback(() => {
    const hasReservations =
      (initialData?.currentlySelectedEvent?.takenSpaces ?? 0) > 0;
    const message = hasReservations
      ? "Всички резервации за този клас ще бъдат маркирани като изтрити. Наистина ли искате да го изтриете?"
      : "Наистина ли искате да изтриете този клас?";

    showCustomConfirmDialog(message, deleteEvent, () => {}, "warning", "Да", "Не");
  }, [deleteEvent, initialData?.currentlySelectedEvent?.takenSpaces]);

  useAddAdditionalLayoutButton({
    iconType: IconType.DeleteIcon,
    buttonOnClick: deleteEventConfirm,
    validator: id !== undefined,
  });

  const successMessage = `Класът беше ${id ? "запазен" : "добавен"} успешно.`;

  const pageTitle = `${
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране"
  } на клас`;

  const buttonTitle =
    pageMode === "add"
      ? "Добавяне"
      : pageMode === "edit"
      ? "Редактиране"
      : "Клониране";

  const onTrySave: SubmitHandler<IClassWizardFormModel> = async (data) => {
    if (
      pageMode === "edit" &&
      initialData?.currentlySelectedEvent?.isRecurring
    ) {
      showCustomConfirmDialog(
        "Искате ли да приложите промяната за всички бъдещи класове от поредицата? Имайте предвид, че всички класове, които са променени, няма да бъдат засегнати.",
        () => onSave(data, true),
        () => onSave(data, false),
        "warning",
        "Да",
        "Не"
      );
    } else {
      onSave(data, false);
    }
  };

  const onSave = async (
    data: IClassWizardFormModel,
    applyToFuture: boolean
  ) => {
    if (applyToFuture) {
      data.applyChangeToAllFutureEvents = true;
    }

    // planTypeIds carries UI sentinels: 0 = "Всички" (All), -1 = "Никакви" (None).
    // Derive the payment mode from them, then strip the sentinels so only real ids remain.
    const rawPlanTypeIds = data.planTypeIds ?? [];
    const isNone = rawPlanTypeIds.includes(NONE_PLAN_TYPES_SENTINEL);
    const specificPlanTypeIds = rawPlanTypeIds.filter(
      (x) => x !== ALL_PLAN_TYPES_SENTINEL && x !== NONE_PLAN_TYPES_SENTINEL,
    );
    const paymentMode = isNone
      ? SubscriptionPaymentMode.None
      : specificPlanTypeIds.length > 0
        ? SubscriptionPaymentMode.Specific
        : SubscriptionPaymentMode.All;
    data.planTypeIds = specificPlanTypeIds;

    // A None event accepts no subscription plan, so paying a prepayment with one is
    // impossible — force the flag off regardless of any earlier value.
    if (paymentMode === SubscriptionPaymentMode.None) {
      data.allowSubscriptionPrepayment = false;
    }

    if (data.date && initialData?.studioSettings) {
      const offsetMinutes = toZonedTime(
        data.date,
        initialData.studioSettings.timezone
      ).getTimezoneOffset();

      // Adjust manually
      const adjustedDate = new Date(
        data.date.getTime() - offsetMinutes * 60 * 1000
      );

      data.date = adjustedDate;
    }

    // Reconcile every "switch + details" option (prepayment provider/link, in-app form
    // vs external link, Multisport override) so the API never receives a detail whose
    // switch is off — see classWizardOptionsUtility.
    const normalizedOptions = normalizeClassWizardOptions(data);

    // The form models the positive ("will the class take place?"); the API persists
    // the inverse cancellation flag. willTakePlace itself is dropped from the payload.
    const { willTakePlace, hasCustomMultisportPrice, ...payloadData } = data;
    const payload = {
      ...payloadData,
      ...normalizedOptions,
      isCancelled: willTakePlace === false,
      paymentMode,
    };

    mutate(
      {
        endpoint: endpointsMetadata.planner.wizard,
        globalIsLoading: true,
        method: "post",
        payload: payload,
        invalidateKeys: [
          [endpointsMetadata.planner.getAll],
          [endpointsMetadata.planner.getEvent],
          [endpointsMetadata.planner.getSignUpEventData],
        ],
        successMessage: successMessage,
      },
      {
        onSuccess: (savedEventId) => {
          // A remote class emails its clients the Meet link; tell the staff member about
          // the ones with no email. A new class carries whoever was registered with it
          // (checked for everyone on it); an edit matters when another client was put on an
          // individual event, or when the class has just become remote.
          if (payload.isRemote) {
            if (pageMode !== "edit") {
              warnClientsWithoutEmail(Number(savedEventId));
            } else if (
              payload.type === EventType.Individual &&
              !!payload.individualUserId &&
              payload.individualUserId !==
                initialData?.currentlySelectedEvent?.individualUserId
            ) {
              warnClientsWithoutEmail(Number(savedEventId), [
                payload.individualUserId,
              ]);
            } else if (!initialData?.currentlySelectedEvent?.isRemote) {
              // The class has just been made remote: everyone on it gets the link now.
              warnClientsWithoutEmail(Number(savedEventId));
            }
          }

          navigate(
            getClassWizardSavedRoute({
              savedEventId: Number(savedEventId),
              appliedToFuture: applyToFuture,
              employeeId: payload.employeeId,
              currentUserId: currentUser?.id,
              othersEventsLocked: initialData?.othersEventsLocked,
            })
          );
        },
      }
    );
  };

  const date = form.watch("date");
  const type = form.watch("type");

  const dateText = useMemo(() => {
    return (
      !!date &&
      `Дата: ${String(date?.getDate()).padStart(2, "0")}/${String(
        date?.getMonth() + 1
      ).padStart(2, "0")}/${date?.getFullYear()}`
    );
  }, [date]);

  const isIndividualEvent = useMemo(
    () => type === EventType.Individual,
    [type]
  );

  const tenantSupportsMultisport =
    !!initialData?.studioSettings?.paymentTypes.find(
      (x) => x.paymentType === PaymentTypes.Multisport
    );
  return {
    form,
    pageTitle,
    buttonTitle,
    dateText,
    initialData,
    studioInstructors,
    isIndividualEvent,
    enableMultisport: tenantSupportsMultisport,
    eventTemplates,
    onTrySave,
  };
};
